import { significantTokens } from '../../json/lexer';
import { TokenKind } from '../../json/tokens';
import { type JsonNode, walk } from '../../json/tree';
import type { LintCheck, LintInput } from '../check';
import type { DiagnosticDraft, Span } from '../diagnostic';

/**
 * What is true of the document only once you know its shape: the same key twice, a number
 * the platform cannot hold, and text after the root value.
 *
 * All three need the tree, so all three are skipped when there is none — which is exactly
 * right: with the depth guard tripped, `checks/structure.ts` has already said the one
 * useful thing there is to say about the document.
 */

/** Integers wider than this cannot be trusted to survive `Number`. */
const SAFE_INTEGER_DIGITS = /^-?[0-9]+$/;

function spanOf(node: JsonNode): Span {
  return { from: node.from, to: node.to };
}

/**
 * Text after the root value.
 *
 * Derived from the tree rather than from jsonc's `EndOfFileExpected`, so the span covers
 * the whole tail — `{"a":1}{"b":2}` underlines the second object, not just its brace.
 */
function trailingContent(
  input: LintInput,
  tree: JsonNode,
  emit: (draft: DiagnosticDraft) => void,
): void {
  const rest = significantTokens(input.tokens).filter(
    (token) => token.kind !== TokenKind.EOF && token.from >= tree.to,
  );

  const first = rest[0];
  const last = rest[rest.length - 1];
  if (first === undefined || last === undefined) return;

  // `007` lexes as three adjacent `Number` tokens and the root is only the first of them.
  // That is a number-literal problem — `checks/tokens.ts` reports it as a leading zero —
  // and calling the rest of the digits "trailing content" would be a second diagnostic for
  // the same typo.
  if (
    tree.kind === 'number' &&
    first.from === tree.to &&
    (first.kind === TokenKind.Number || first.kind === TokenKind.Unknown)
  ) {
    return;
  }

  emit({ code: 'syntax/trailing-content', span: { from: first.from, to: last.to } });
}

/**
 * The same key twice in one object.
 *
 * Compared by *value*, not by source text, so `{"a":1,"a":2}` is the duplicate it
 * really is. The diagnostic is anchored on the later key and carries the earlier one as a
 * related span, because which of the two values to keep is a decision about data that the
 * engine refuses to make for the user (ADR-0008).
 *
 * `RelatedSpan.labelCode` is a `MessageCode`, which is `DiagnosticCode | fix.*` and nothing
 * else — so the label of the related span is the diagnostic's own code. The message catalog
 * therefore renders one template for the problem and reuses it for the pointer.
 */
function duplicateKeys(tree: JsonNode, emit: (draft: DiagnosticDraft) => void): void {
  walk(tree, (node) => {
    if (node.kind !== 'object') return;

    const seen = new Map<string, JsonNode>();

    for (const property of node.children ?? []) {
      if (property.kind !== 'property') continue;

      // A property with a missing value (`{"a":}`) really does arrive with one child.
      const key = property.children?.[0];
      if (key === undefined || key.kind !== 'string' || typeof key.value !== 'string') continue;

      const first = seen.get(key.value);
      if (first === undefined) {
        seen.set(key.value, key);
        continue;
      }

      emit({
        code: 'semantic/duplicate-key',
        span: spanOf(key),
        params: { key: key.value },
        related: [
          {
            span: spanOf(first),
            labelCode: 'semantic/duplicate-key',
            params: { key: key.value },
          },
        ],
      });
    }
  });
}

/**
 * Numbers a double cannot hold.
 *
 * Reported for the three losses that are both detectable and surprising: an integer wider
 * than the mantissa (`9007199254740993` parses to `…992`), an exponent that overflows to
 * `Infinity`, and one that underflows to `0`. Ordinary fractions are *not* reported —
 * `0.1` is inexact in every IEEE-754 language there is, and saying so on every decimal in
 * the file would be noise rather than information.
 *
 * `parsed` is a string, not a number: `params` must survive `structuredClone` *and* be
 * writable in a `.expected.json` fixture, and `Infinity` is not JSON.
 */
function precisionLoss(
  input: LintInput,
  tree: JsonNode,
  emit: (draft: DiagnosticDraft) => void,
): void {
  walk(tree, (node) => {
    if (node.kind !== 'number') return;

    const parsed = node.value;
    if (typeof parsed !== 'number') return;

    const text = input.source.slice(node.from, node.to);

    if (!Number.isFinite(parsed)) {
      emit({
        code: 'number/precision-loss',
        span: spanOf(node),
        params: { text, parsed: String(parsed) },
      });
      return;
    }

    if (SAFE_INTEGER_DIGITS.test(text)) {
      if (!Number.isInteger(parsed)) return;
      if (BigInt(text) === BigInt(parsed)) return;

      emit({
        code: 'number/precision-loss',
        span: spanOf(node),
        params: { text, parsed: String(parsed) },
      });
      return;
    }

    // Underflow: a mantissa with a significant digit that nonetheless parses to zero.
    const mantissa = text.split(/[eE]/)[0] ?? '';
    if (parsed === 0 && /[1-9]/.test(mantissa)) {
      emit({
        code: 'number/precision-loss',
        span: spanOf(node),
        params: { text, parsed: String(parsed) },
      });
    }
  });
}

export const semanticsCheck: LintCheck = {
  id: 'semantics',

  run(input, emit) {
    const { tree } = input;
    if (tree === undefined) return;

    trailingContent(input, tree, emit);
    if (input.options.detectDuplicateKeys) duplicateKeys(tree, emit);
    if (input.options.detectPrecisionLoss) precisionLoss(input, tree, emit);
  },
};
